/** Mention token syntax: `@[Display Name](user:<id>)`. */

const TOKEN_RE = /@\[([^\]]+)\]\(user:(\d+)\)/g;

export interface MentionToken {
  /** Display name as it appeared in the comment body. */
  name: string;
  /** Mentioned user id. */
  userId: number;
}

/** Strip mention tokens to a human-readable string ("@Name" instead of the raw token). */
export function renderBody(body: string): string {
  return body.replace(TOKEN_RE, (_match, name: string) => `@${name}`);
}

/** Href scheme a mention link carries once the body is rendered as Markdown. */
export const MENTION_HREF_PREFIX = 'user:';

// Code a Markdown renderer shows literally: fenced blocks and inline code spans.
const CODE_RE = /(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/;

/** Rewrite mention tokens as Markdown links whose text keeps the "@"
 *  (`[@Name](user:12)`), so a Markdown renderer can show them as mentions.
 *  Inside code, where a link would show as raw syntax, a token becomes "@Name". */
export function mentionsToLinks(body: string): string {
  return body
    .split(CODE_RE)
    .map((part, i) =>
      i % 2
        ? renderBody(part)
        : part.replace(
            TOKEN_RE,
            (_match, name: string, id: string) => `[@${name}](${MENTION_HREF_PREFIX}${id})`,
          ),
    )
    .join('');
}

/** Pull every mention token out of a body — handy for previews/highlights. */
export function findMentions(body: string): MentionToken[] {
  const out: MentionToken[] = [];
  for (const m of body.matchAll(TOKEN_RE)) {
    out.push({ name: m[1], userId: Number(m[2]) });
  }
  return out;
}

/**
 * Look back from `caret` for an in-progress mention trigger (`@partial` with
 * no whitespace between the `@` and the caret). Returns the `partial` text and
 * the index of the `@`, or null if no trigger is active.
 */
export function findMentionTrigger(body: string, caret: number): { start: number; partial: string } | null {
  const slice = body.slice(0, caret);
  const at = slice.lastIndexOf('@');
  if (at < 0) return null;
  // Anything between `@` and the caret must be a contiguous name chunk.
  const partial = slice.slice(at + 1);
  if (/\s/.test(partial)) return null;
  // Must be a fresh `@` (start of string or preceded by whitespace).
  if (at > 0 && !/\s/.test(slice.charAt(at - 1))) return null;
  return { start: at, partial };
}

/** Replace the `@partial` trigger with a fully-formed token + a trailing space. */
export function insertMention(
  body: string,
  trigger: { start: number; partial: string },
  caret: number,
  name: string,
  userId: number,
): { body: string; caret: number } {
  const before = body.slice(0, trigger.start);
  const after = body.slice(caret);
  const token = `@[${name}](user:${userId}) `;
  const next = before + token + after;
  return { body: next, caret: before.length + token.length };
}
